import React, {
  useState,
  useRef,
  useMemo,
  memo,
  useCallback,
  useEffect,
} from "react";
import { graphql } from "gatsby";
import Section from "../components/section";
import GlobalStyle from "../styles/globalStyles";
import ThreeJsComponent from "../3D/app";
import LandingNavigation from "../components/landingNavigation";
import Loader from "../components/loader";
import {
  IndexWrapper,
  FixedLayerContainer,
  LenisRoot,
  LenisScroll,
} from "../styles/components/landing";
import Lenis from "@studio-freight/lenis";
import animations from "../animations/animations";
import { getDeviceInfo } from "../data/utils";

const MemoizedThreeJsComponent = memo(ThreeJsComponent);

const IndexPage = ({ data }) => {
  const cameraRef = useRef(null);
  const planesRef = useRef([]);
  const sceneRef = useRef(null);
  const lenisScrollRef = useRef(null);
  const lenisRootRef = useRef(null);
  const lenisRef = useRef(null);
  const [zoomingAnimation, setZoomingAnimation] = useState(true);
  const [planesAnimating, setPlanesAnimating] = useState(false);
  const [scrollReady, setScrollReady] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const storyContent = data?.allDatoCmsBespokeStory?.edges[0]?.node?.content;

  const loaderComponent = useMemo(() => {
    return Object.values(storyContent).filter(
      component => component.key === "component--loader"
    );
  }, [storyContent]);

  const sectionsComponent = useMemo(() => {
    return Object.values(storyContent).filter(
      component => component.key === "component--sections"
    );
  }, [storyContent]);

  const sectionContent = useMemo(() => {
    return sectionsComponent.flatMap(section => {
      return section.content.map((item, itemIndex) => {
        const contentItems = item.content.reduce((acc, innerItem) => {
          if (innerItem?.moduleTitle === "component--status") {
            acc.status = innerItem?.json || null;
          } else if (innerItem?.moduleTitle === "component--section-image") {
            acc.mainImage = innerItem?.images || null;
          } else if (
            innerItem?.moduleTitle === "component--section-navigation"
          ) {
            acc.navigationImage = innerItem?.images || null;
          } else if (innerItem?.moduleTitle === "component--colour") {
            acc.colors = innerItem?.json || null;
          } else if (innerItem?.moduleTitle === "component--section-heading") {
            acc.heading = innerItem?.text || innerItem?.images || null;
          } else if (innerItem?.moduleTitle === "component--section-video") {
            acc.video = innerItem?.muxvideo || null;
          } else if (innerItem?.moduleTitle === "component--section-cursor") {
            acc.cursor = innerItem?.images || null;
          } else if (innerItem?.__typename === "DatoCmsCta") {
            acc.link = innerItem?.url || null;
          }
          return acc;
        }, {});

        const active = JSON.parse(contentItems.status).active;
        const colors = contentItems.colors
          ? JSON.parse(contentItems.colors)
          : null;

        const sectionBodega = item.key.includes("-bodega");

        return {
          key: `${item.key}-${itemIndex}`,
          active: active,
          sectionBodega: sectionBodega,
          sectionIndex: itemIndex,
          colors: colors?.color || null,
          heading: contentItems.heading,
          mainImage: contentItems.mainImage,
          navigationImage: contentItems.navigationImage,
          cursor: contentItems.cursor,
          video: contentItems.video,
          link: contentItems.link,
        };
      });
    });
  }, [sectionsComponent]);

  const handleSetZoomingAnimation = useCallback(val => {
    setZoomingAnimation(prev => (prev !== val ? val : prev));
  }, []);

  const handleSetPlanesAnimating = useCallback(val => {
    setPlanesAnimating(prev => (prev !== val ? val : prev));
  }, []);

  const handleLoaded = useCallback(val => {
    setIsLoaded(prev => (prev !== val ? val : prev));
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const { isMobile } = getDeviceInfo();

    if (isMobile) {
      return;
    }

    if (!lenisScrollRef.current || !lenisRootRef.current || isMobile) return;

    const wrapper = lenisRootRef.current;
    const content = lenisScrollRef.current;

    const lenis = new Lenis({
      wrapper,
      content,
      lerp: 0.1,
      smoothWheel: true,
      smoothTouch: true,
      touchMultiplier: 1.5,
      syncTouch: true,
      autoRaf: false,
    });

    lenisRef.current = lenis;

    lenisRef.current.on("scroll", animations.ScrollTrigger.update);

    animations.ScrollTrigger.scrollerProxy(content, {
      scrollTop(value) {
        return arguments.length
          ? lenisRef.current.scrollTo(value, { immediate: true })
          : lenisRef.current.scroll;
      },
      getBoundingClientRect() {
        return {
          top: 0,
          left: 0,
          width: window.innerWidth,
          height: window.innerHeight,
        };
      },
      pinType: content.style.transform ? "transform" : "fixed",
    });

    const raf = time => lenisRef.current.raf(time * 1000);
    animations.ticker.add(raf);
    animations.ticker.lagSmoothing(0);

    setTimeout(() => {
      animations.ScrollTrigger.refresh();
      setScrollReady(true);
    }, 100);

    return () => {
      animations.ticker.remove(raf);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || isLoaded) return;
    const { isMobile } = getDeviceInfo();

    if (isMobile) {
      setScrollReady(true);
      return;
    }
  }, [isLoaded]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      animations.ScrollTrigger.refresh();
    }, 300);

    return () => clearTimeout(timeout);
  }, []);

  console.log("Deployed - 23/07");

  return (
    <IndexWrapper>
      <Loader
        content={loaderComponent}
        lenisRef={lenisRef}
        handleLoaded={handleLoaded}
      />
      <LenisRoot ref={lenisRootRef} id="lenis-root">
        <GlobalStyle />
        {storyContent && (
          <LenisScroll ref={lenisScrollRef} id="lenis-scroll">
            <Section
              datoContentData={storyContent}
              handleSetZoomingAnimation={handleSetZoomingAnimation}
              cameraRef={cameraRef}
              planesRef={planesRef}
              sceneRef={sceneRef}
              scrollReady={scrollReady}
            />
            <FixedLayerContainer className="app-wrapper">
              <LandingNavigation content={sectionContent} />
              <MemoizedThreeJsComponent
                datoContentData={sectionContent}
                zoomingAnimation={zoomingAnimation}
                planesAnimating={planesAnimating}
                scrollReady={scrollReady}
                lenisRef={lenisRef}
                handleSetPlanesAnimating={handleSetPlanesAnimating}
                cameraRef={cameraRef}
                planesRef={planesRef}
                sceneRef={sceneRef}
              />
            </FixedLayerContainer>
          </LenisScroll>
        )}
      </LenisRoot>
    </IndexWrapper>
  );
};

export default IndexPage;

export const query = graphql`
  query StoryQuery {
    allDatoCmsBespokeStory(
      filter: { id: { eq: "DatoCmsBespokeStory-ZjJc17feSUSjgrHkkUr9rA-en" } }
    ) {
      edges {
        node {
          id
          title
          heroImage {
            fluid {
              src
              srcSet
              aspectRatio
            }
          }
          content {
            ...DatoAgeGateFragment
            ...DatoArticleAggregatorFragment
            ...DatoCreditFragment
            ...DatoCtaFragment
            ...DatoCustomMarkupFragment
            ...DatoGalleryFragment
            ...DatoHeadlineFragment
            ...DatoHeroFragment
            ...DatoImageFragment
            ...DatoImageTextFragment
            ...DatoInterviewFragment
            ...DatoLayoutDividerFragment
            ...DatoLoaderFragment
            ...DatoMarqueeFragment
            ...DatoParagraphFragment
            ...DatoProgressBarFragment
            ...DatoQuoteFragment
            ...DatoSeparatorFragment
            ...DatoShoppingCarouselFragment
            ...DatoSvgFragment
            ...DatoVideoFragment
            ...DatoKeyValuePairFragment
          }
        }
      }
    }
  }
`;
